import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Gift, Sparkles } from 'lucide-react';

/**
 * TutorialRewardModal
 *
 * Displayed when the player completes the tutorial, showing the 5000 primo reward
 * and encouraging them to pull for supports.
 */
const TutorialRewardModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black bg-opacity-60"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
          className="relative bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Decorative stars */}
          <div className="absolute top-4 right-4 text-amber-400 opacity-50">
            <Sparkles size={32} />
          </div>
          <div className="absolute top-8 left-4 text-yellow-400 opacity-30">
            <Star size={24} />
          </div>
          <div className="absolute bottom-8 right-8 text-amber-300 opacity-40">
            <Star size={20} />
          </div>

          {/* Content */}
          <div className="relative p-8">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full p-6 shadow-lg"
              >
                <Gift size={48} className="text-white" />
              </motion.div>
            </div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-center text-amber-900 mb-2"
            >
              Tutorial Complete!
            </motion.h2>

            {/* Reward display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white bg-opacity-60 rounded-2xl p-6 mb-4 text-center shadow-inner"
            >
              <p className="text-sm text-amber-700 mb-2">You've received</p>
              <div className="flex items-center justify-center gap-2">
                <Star size={32} className="text-amber-500" fill="currentColor" />
                <span className="text-5xl font-bold text-amber-600">2,000</span>
              </div>
              <p className="text-lg font-semibold text-amber-800 mt-1">Primos!</p>
            </motion.div>

            {/* Encouragement text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mb-6"
            >
              <p className="text-pocket-text-light text-sm leading-relaxed">
                Use your Primos to pull for powerful <span className="font-semibold text-pocket-blue">Support Cards</span> that will help you train your Pokemon and boost their stats!
              </p>
            </motion.div>

            {/* Continue button */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              onClick={onClose}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-bold text-lg shadow-lg hover:from-amber-600 hover:to-yellow-600 transition-all transform hover:scale-105"
            >
              Let's Go!
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TutorialRewardModal;
